import { Fragment, useState } from 'react';
import type { Order, OrderStatus } from '@harbor/shared';
import { nextOrderStatuses } from '@harbor/shared';
import { ApiError, apiRequest } from '../../lib/api.js';
import { useApi, useDebounced } from '../../lib/hooks.js';
import { money, dateTime, titleCase, orderStatusTone, relativeTime, currencyOf } from '../../lib/format.js';
import { Alert, Badge, Button, Card, EmptyState, Select, TextInput } from '../../components/ui.js';

interface AdminOrderRow {
  id: string;
  number: string;
  email: string;
  status: OrderStatus;
  totalMinor: number;
  currency: string;
  placedAt: string;
  itemCount: number;
}

interface TimelineEntry {
  id: string;
  actorEmail: string | null;
  action: string;
  metadata: unknown;
  createdAt: string;
}

/**
 * Order management.
 *
 * ## Transitions come from the shared state machine
 *
 * The buttons rendered for an order are exactly `nextOrderStatuses(status)`. The
 * API validates against the same map. That means an operator can never click a
 * button the server will reject, and adding a state to the machine updates both
 * sides at once — there is no second list of allowed transitions to keep in
 * sync.
 *
 * ## The timeline is the audit log
 *
 * Status changes are read back from `audit_log`, not from a separate history
 * table. One append-only record of who did what serves both the operator's
 * question ("why is this cancelled?") and the compliance one.
 */
export function AdminOrders() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const params = new URLSearchParams();
  if (statusFilter !== 'all') params.set('status', statusFilter);
  if (debouncedSearch) params.set('q', debouncedSearch);
  const query = params.toString();

  const { data, loading, refetch } = useApi<{ orders: AdminOrderRow[] }>(
    `/api/admin/orders${query ? `?${query}` : ''}`,
  );

  async function transition(orderId: string, status: OrderStatus) {
    setBusy(orderId);
    setError(null);
    try {
      await apiRequest(`/api/admin/orders/${orderId}/status`, {
        method: 'POST',
        body: { status, note: '' },
      });
      refetch();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.problem.detail : 'Could not update the order');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Orders</h1>
        <p className="mt-1 text-sm text-muted">
          Fulfilling an order does not return stock. Refunding or cancelling does — the units go back
          into the catalogue with a ledger entry.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="w-full max-w-xs">
          <TextInput
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by order number or email"
            aria-label="Search orders"
          />
        </div>
        <div className="w-48">
          <Select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            <option value="pending_payment">Awaiting payment</option>
            <option value="paid">Paid</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </Select>
        </div>
      </div>

      {error && (
        <Alert tone="danger" title="Could not update the order" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="skeleton h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : data && data.orders.length === 0 ? (
        <EmptyState
          title="No orders match"
          description="Try a different status filter, or place an order from the storefront using the tok_visa test card."
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[44rem] text-sm">
              <thead>
                <tr className="border-b border-line bg-raised text-left text-xs uppercase tracking-wide text-subtle">
                  <th scope="col" className="px-4 py-3 font-medium">Order</th>
                  <th scope="col" className="px-4 py-3 font-medium">Customer</th>
                  <th scope="col" className="px-4 py-3 text-right font-medium">Items</th>
                  <th scope="col" className="px-4 py-3 text-right font-medium">Total</th>
                  <th scope="col" className="px-4 py-3 font-medium">Status</th>
                  <th scope="col" className="px-4 py-3 text-right font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data?.orders.map((order) => {
                  const transitions = nextOrderStatuses(order.status) as OrderStatus[];
                  const isOpen = expanded === order.id;

                  return (
                    <Fragment key={order.id}>
                      <tr className="hover:bg-raised">
                        <td className="px-4 py-3">
                          <p className="tabular font-medium">{order.number}</p>
                          <p className="text-xs text-subtle">{relativeTime(order.placedAt)}</p>
                        </td>
                        <td className="px-4 py-3 text-muted">{order.email}</td>
                        <td className="tabular px-4 py-3 text-right">{order.itemCount}</td>
                        <td className="tabular px-4 py-3 text-right font-medium">
                          {money(order.totalMinor, currencyOf(order.currency))}
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={orderStatusTone(order.status)}>
                            {titleCase(order.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpanded(isOpen ? null : order.id)}
                            aria-expanded={isOpen}
                          >
                            {isOpen ? 'Hide' : 'Manage'}
                          </Button>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="bg-raised/60">
                          <td colSpan={6} className="px-4 py-5">
                            <div className="grid gap-5 lg:grid-cols-2">
                              <div>
                                <h3 className="text-xs font-medium uppercase tracking-wide text-subtle">
                                  Allowed transitions
                                </h3>
                                {transitions.length === 0 ? (
                                  <p className="mt-2 text-sm text-muted">
                                    {titleCase(order.status)} is terminal. No further changes are
                                    possible.
                                  </p>
                                ) : (
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {transitions.map((next) => (
                                      <Button
                                        key={next}
                                        size="sm"
                                        variant={next === 'cancelled' || next === 'refunded' ? 'danger' : 'primary'}
                                        loading={busy === order.id}
                                        onClick={() => void transition(order.id, next)}
                                      >
                                        Mark {titleCase(next)}
                                      </Button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <OrderTimeline orderId={order.id} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * Fetches the order detail only when a row is expanded.
 *
 * The list endpoint deliberately does not return line items or history — doing so
 * for fifty rows would be fifty unnecessary joins on every page load.
 */
function OrderTimeline({ orderId }: { orderId: string }) {
  const { data, loading } = useApi<{ order: Order; timeline: TimelineEntry[] }>(
    `/api/admin/orders/${orderId}`,
  );

  if (loading && !data) {
    return <div className="skeleton h-24 w-full rounded-lg" />;
  }
  if (!data) return null;

  return (
    <div>
      <h3 className="text-xs font-medium uppercase tracking-wide text-subtle">
        Items and history
      </h3>
      <ul className="mt-2 space-y-1 text-sm">
        {data.order.items.map((item) => (
          <li key={item.id} className="flex justify-between gap-3">
            <span className="truncate text-muted">
              {item.quantity} × {item.name}
            </span>
            <span className="tabular">{money(item.lineTotalMinor, data.order.currency)}</span>
          </li>
        ))}
      </ul>

      {data.timeline.length > 0 && (
        <ol className="mt-4 space-y-2 border-t border-line pt-3 text-xs">
          {data.timeline.map((entry) => (
            <li key={entry.id} className="flex items-start gap-2">
              <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
              <div>
                <p className="text-muted">
                  <span className="font-medium text-ink">{entry.action}</span>
                  {entry.actorEmail ? ` by ${entry.actorEmail}` : ' by the payment provider'}
                </p>
                <p className="tabular text-subtle">{dateTime(entry.createdAt)}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
